import * as Icons from "@nova/component-icons"
import { LiveChatPayload } from "@nova/constants"
import { FunctionComponent, useState, useEffect, useRef } from "react"

const FADE_OUT_DELAY = 5000

const MapboxCursorWithMessage = ({
    name,
    color,
    zIndex,
    message,
}: {
    name: string
    color: string
    zIndex: number
    message: string
}) => (
    <>
        <Icons.Cursor height={16} width={16} fill={color} />
        <div
            className={"absolute px-3 py-2 rounded-r-lg rounded-bl-lg ml-3 w-max shadow-dp3"}
            style={{ backgroundColor: color, zIndex }}
        >
            <small className="text-white-600">{name}</small>

            <h4 key={message} className="text-gray-0">
                {message.slice(-40)}
            </h4>
        </div>
    </>
)

interface MapboxCursorProps {
    color: string
    name: string
    zIndex: number
    liveChat?: LiveChatPayload
}

const MapboxCursor: FunctionComponent<MapboxCursorProps> = ({ liveChat, color, ...rest }) => {
    const [timedOut, setTimedOut] = useState(false)
    const timeoutRef = useRef<number>(undefined)

    useEffect(() => {
        timeoutRef.current = window.setTimeout(() => setTimedOut(true), FADE_OUT_DELAY)
        return () => window.clearTimeout(timeoutRef.current)
    }, [])

    useEffect(() => {
        if (!liveChat?.message) return

        setTimedOut(false)
        const timeout = setTimeout(() => setTimedOut(true), FADE_OUT_DELAY)

        return () => clearTimeout(timeout)
    }, [liveChat?.message])

    return !timedOut && liveChat ? (
        <MapboxCursorWithMessage key={1} message={liveChat.message} color={color} {...rest} />
    ) : (
        <MapboxCursorDefault key={1} color={color} {...rest} />
    )
}

interface MapboxCursorDefaultProps {
    color: string
    name: string
    zIndex: number
}

const MapboxCursorDefault: FunctionComponent<MapboxCursorDefaultProps> = ({
    color,
    name,
    zIndex,
}) => (
    <>
        <Icons.Cursor height={16} width={16} fill={color} />
        <div
            className={"absolute px-1.5 py-0.5 rounded-md ml-3 text-gray-0 w-max"}
            style={{ backgroundColor: color, zIndex }}
        >
            <small>{name}</small>
        </div>
    </>
)

MapboxCursor.displayName = "MapboxCursor"
export { MapboxCursor }
